import { useEffect, useState } from 'react';
import { ApiError, getAnalytics } from '@/api/client';
import { getApiBaseUrl } from '@/api/baseUrl';
import { POLL_INTERVAL_SERVICES_ALERTS_MS, useRefresh } from '@/context/RefreshContext';
import type { AnalyticsResponse, Environment } from '@/types/api';
import { formatDateTime } from '@/utils/format';
import { FunnelWidget } from '@/components/FunnelWidget';
import { CampaignsWidget } from '@/components/CampaignsWidget';
import { MerchantsWidget } from '@/components/MerchantsWidget';
import { CohortsWidget } from '@/components/CohortsWidget';
import { QualityWidget } from '@/components/QualityWidget';
import { OrdersWidget } from '@/components/OrdersWidget';
import { UserActivityWidget } from '@/components/UserActivityWidget';

interface AnalyticsTabProps {
  environment: Environment;
}

const formatWhen = (iso: string): string =>
  formatDateTime(iso, { dateStyle: 'medium', timeStyle: 'short' });

function formatDuration(secs: number): string {
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatBounceRate(decimal: number): string {
  return `${(decimal * 100).toFixed(1)}%`;
}

function capitalizePlatform(platform: string): string {
  if (platform === 'ios') return 'iOS';
  return platform.charAt(0).toUpperCase() + platform.slice(1);
}

function formatApiErrorBody(body: unknown): string | null {
  if (body === undefined || body === null) return null;
  if (typeof body !== 'string') {
    try { return JSON.stringify(body); } catch { return Object.prototype.toString.call(body); }
  }
  const t = body.trim();
  if (t === '') return null;
  try {
    const o = JSON.parse(t) as { message?: unknown; error?: unknown };
    const msg = o.message ?? o.error;
    if (typeof msg === 'string' && msg.trim() !== '') return msg.trim();
  } catch { /* not JSON */ }
  const max = 500;
  return t.length > max ? `${t.slice(0, max)}…` : t;
}

function appendClientFetchFailureHint(err: Error, lines: string[]): void {
  if (err.name !== 'TypeError') return;
  const m = err.message.trim().toLowerCase();
  const looksLikeFetchFailure =
    m === 'failed to fetch' ||
    m === 'load failed' ||
    m.includes('failed to fetch') ||
    m.includes('load failed') ||
    m.includes('networkerror');
  if (!looksLikeFetchFailure) return;
  lines.push(
    'Hint: The browser never received an HTTP response. Typical causes include API or network outage, CORS blocking ops.blooph.com, TLS/certificate issues, VPN or proxy, or the /analytics endpoint not being deployed. Check that GET /analytics is wired in API Gateway with CORS and JWT authorizer like other routes.',
  );
}

function formatAnalyticsLoadError(err: unknown): string {
  const lines: string[] = [];
  try {
    lines.push(`API base: ${getApiBaseUrl()}`);
  } catch (configErr: unknown) {
    lines.push(`Configuration: ${configErr instanceof Error ? configErr.message : String(configErr)}`);
  }
  lines.push('Request: GET /analytics');
  if (err instanceof ApiError) {
    lines.push(`HTTP status: ${err.status}`);
    lines.push(`Details: ${err.message}`);
    const bodyText = formatApiErrorBody(err.body);
    if (bodyText !== null) lines.push(`Response body: ${bodyText}`);
    if (err.status === 401) {
      lines.push('Hint: Session may have expired — try logging out and signing in again.');
    } else if (err.status === 403) {
      lines.push('Hint: Your account may not be authorized to access analytics data from the API.');
    } else if (err.status === 404) {
      lines.push('Hint: The /analytics endpoint is not deployed yet — deploy observability-api Stage 6.');
    } else if (err.status === 503) {
      lines.push('Hint: Google Analytics data is temporarily unavailable. This may resolve in a moment.');
    }
    return lines.join('\n');
  }
  if (err instanceof Error) {
    lines.push(`Client error (${err.name}): ${err.message}`);
    appendClientFetchFailureHint(err, lines);
    return lines.join('\n');
  }
  lines.push(`Unknown error: ${String(err)}`);
  return lines.join('\n');
}

export function AnalyticsTab({ environment }: AnalyticsTabProps): JSX.Element {
  const { mode, manualRefreshNonce, tabVisible } = useRefresh();
  const [data, setData] = useState<AnalyticsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!tabVisible) return undefined;

    let cancelled = false;

    function runFetch(silent: boolean): void {
      if (!silent) {
        setIsLoading(true);
        setError(null);
      }
      void getAnalytics()
        .then((response) => {
          if (!cancelled) setData(response);
        })
        .catch((err: unknown) => {
          if (cancelled || silent) return;
          setError(formatAnalyticsLoadError(err));
        })
        .finally(() => {
          if (!cancelled && !silent) setIsLoading(false);
        });
    }

    runFetch(false);

    let intervalId: number | undefined;
    if (mode === 'auto') {
      intervalId = window.setInterval(() => runFetch(true), POLL_INTERVAL_SERVICES_ALERTS_MS);
    }

    return () => {
      cancelled = true;
      if (intervalId !== undefined) window.clearInterval(intervalId);
    };
  }, [mode, tabVisible, manualRefreshNonce]);

  return (
    <div className="analytics-tab">
      <h2>Analytics</h2>
      <p className="tab-lead">
        Google Analytics metrics for the Blooph platform. Active user counts are real-time and rolling windows from GA4.
      </p>

      {/*
        Analytics v2 marketing widgets (CORE-958 funnel, CORE-959 campaigns,
        CORE-960 merchants, CORE-962 cohorts). Rendered above the legacy
        GA4 metrics; legacy sections stay until the ADS-058 cutover.
      */}
      <FunnelWidget />
      <CampaignsWidget />
      <MerchantsWidget />
      <CohortsWidget />

      {/*
        Engineering-lane widget (CORE-961 data quality panel).
        Env-scoped — receives the dashboard's global environment selector.
      */}
      <QualityWidget />
      <OrdersWidget environment={environment} />
      <UserActivityWidget environment={environment} />

      {isLoading && <p className="placeholder">Loading analytics data...</p>}
      {!isLoading && error !== null && (
        <div className="analytics-error" role="alert">
          <p className="analytics-error-title">Could not load analytics data</p>
          <pre className="analytics-error-detail">{error}</pre>
        </div>
      )}
      {!isLoading && error === null && data !== null && (
        <>
          <p className="fetched-at">Last updated {formatWhen(data.lastUpdated)}</p>

          <section className="analytics-v2-widget analytics-section">
            <h3>Active Users</h3>
            <div className="analytics-stat-grid">
              <div className="analytics-stat">
                <span className="analytics-stat-value">{data.activeUsers.realTime.toLocaleString()}</span>
                <span className="analytics-stat-label">Right now</span>
              </div>
              <div className="analytics-stat">
                <span className="analytics-stat-value">{data.activeUsers.daily.toLocaleString()}</span>
                <span className="analytics-stat-label">Today (DAU)</span>
              </div>
              <div className="analytics-stat">
                <span className="analytics-stat-value">{data.activeUsers.weekly.toLocaleString()}</span>
                <span className="analytics-stat-label">This week (WAU)</span>
              </div>
              <div className="analytics-stat">
                <span className="analytics-stat-value">{data.activeUsers.monthly.toLocaleString()}</span>
                <span className="analytics-stat-label">This month (MAU)</span>
              </div>
            </div>
          </section>

          <section className="analytics-v2-widget analytics-section">
            <h3>Traffic <span className="analytics-section-note">(last 7 days)</span></h3>
            <div className="analytics-stat-grid">
              <div className="analytics-stat">
                <span className="analytics-stat-value">{data.traffic.sessions.toLocaleString()}</span>
                <span className="analytics-stat-label">Sessions</span>
              </div>
              <div className="analytics-stat">
                <span className="analytics-stat-value">{data.traffic.pageViews.toLocaleString()}</span>
                <span className="analytics-stat-label">Page Views</span>
              </div>
              <div className="analytics-stat">
                <span className="analytics-stat-value">{formatDuration(data.traffic.avgSessionDurationSecs)}</span>
                <span className="analytics-stat-label">Avg Session</span>
              </div>
              <div className="analytics-stat">
                <span className="analytics-stat-value">{formatBounceRate(data.traffic.bounceRate)}</span>
                <span className="analytics-stat-label">Bounce Rate</span>
              </div>
            </div>
          </section>

          <section className="analytics-v2-widget analytics-section">
            <h3>Top Pages</h3>
            {data.topPages.length === 0 ? (
              <p className="empty-state" role="status">No page data available.</p>
            ) : (
              <div className="table-wrap">
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Title</th>
                      <th>Path</th>
                      <th>Views</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topPages.map((page, i) => (
                      <tr key={page.path}>
                        <td className="analytics-rank">{i + 1}</td>
                        <td>{page.title}</td>
                        <td className="analytics-path">{page.path}</td>
                        <td className="analytics-num">{page.views.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="analytics-v2-widget analytics-section">
            <h3>Device Breakdown <span className="analytics-section-note">(last 30 days)</span></h3>
            {data.devices.length === 0 ? (
              <p className="empty-state" role="status">No device data available.</p>
            ) : (
              <div className="table-wrap">
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>Platform</th>
                      <th>Users</th>
                      <th>Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.devices.map((entry) => (
                      <tr key={entry.platform}>
                        <td>{capitalizePlatform(entry.platform)}</td>
                        <td className="analytics-num">{entry.users.toLocaleString()}</td>
                        <td className="analytics-num">{entry.percentage.toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
